<template>
  <v-card flat class="pa-4 mt-1" elevation="2" outlined>
    <!-- Logo -->
    <v-card-title class="d-flex align-center justify-center py-0">
      <div class="d-flex align-center">
        <v-img
          :src="require(`@/assets/images/logos/logo.png`)"
          max-width="220px"
          alt="logo"
          contain
        ></v-img>
      </div>
    </v-card-title>

    <v-form class="multi-col-validation">
      <v-card-text class="pt-1">
        <p class="text-h6 text-center mb-4">Add a Marketing Message</p>

        <v-row>
          <!-- Title -->
          <v-col cols="12" class="py-0 my-1">
            <v-text-field
              v-model="messageTitle"
              ref="message_title"
              outlined
              dense
              label="Message Title *"
              prepend-inner-icon="mdi-format-title"
              hide-details="auto"
            ></v-text-field>
          </v-col>

          <!-- Link (pré-rempli avec le lien de parrainage, bouton copier) -->
          <v-col cols="12" class="py-0 my-1">
            <v-text-field
              v-model="messageLink"
              ref="message_link"
              outlined
              dense
              label="Message Link"
              prepend-inner-icon="mdi-link-variant"
              hide-details="auto"
              :hint="'Your referral link is automatically set – click the copy icon to share'"
              persistent-hint
            >
              <template v-slot:append>
                <v-btn icon small @click="copyLink" title="Copy referral link">
                  <v-icon small>mdi-content-copy</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-col>

          <!-- Content -->
          <v-col cols="12" class="py-0 my-1">
            <v-textarea
              outlined
              v-model="messageContent"
              ref="message_content"
              name="input-7-3"
              label="Message Content *"
              prepend-inner-icon="mdi-text"
              rows="4"
              auto-grow
              hide-details="auto"
            ></v-textarea>
          </v-col>

          <!-- File -->
          <v-col cols="12" class="py-0 my-1">
            <v-file-input
              show-size
              v-model="messageFile"
              ref="message_file"
              truncate-length="18"
              style="cursor: pointer"
              label="Attach a file (image, PDF, etc.)"
              prepend-icon="mdi-paperclip"
              accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.mp4"
              hide-details="auto"
            ></v-file-input>
          </v-col>
        </v-row>
      </v-card-text>

      <div class="text-end">
        <spinner v-if="loading" class="text-end"></spinner>
        <v-btn
          v-else
          color="primary"
          class="me-3 mt-3"
          depressed
          @click="addMessage()"
        >
          <v-icon left>mdi-plus</v-icon>
          Add Message
        </v-btn>
      </div>
    </v-form>
  </v-card>
</template>

<script>
import { mdiKeyOutline, mdiLockOpenOutline, mdiEyeOffOutline, mdiEyeOutline } from '@mdi/js'
import axios from 'axios'
import Spinner from "@/components/Spinner.vue";

export default {
  components: {
    Spinner,
  },
  data() {
    return {
      loading: false,
      messageTitle: "",
      messageLink: "",          // sera pré-rempli avec le lien de parrainage complet
      messageFile: [],
      messageContent: "",

      // icons
      mdiKeyOutline,
      mdiLockOpenOutline,
      mdiEyeOffOutline,
      mdiEyeOutline,
    }
  },

  async mounted() {
    document.title = "TopUp | Add Ad Message";
    await this.loadDefaultReferralLink();
  },

  methods: {
    async loadDefaultReferralLink() {
      try {
        // Récupère le serial_no de l'agent
        const res = await axios.get('/api/v1/topup/my-agent-info/');
        if (res.data && res.data.serial_no) {
          const serial = res.data.serial_no;
          const baseUrl = "https://myapp.pettycashglobal.com";
          // Encode le code comme dans BusinessDashboard.vue
          const payload = `pc_ref:${serial}`;
          const encoded = btoa(payload)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
          this.messageLink = `${baseUrl}/create_business_account2?ref=${encoded}`;
        }
      } catch (err) {
        console.error('Failed to load agent info for referral link', err);
        // Le champ reste vide, l'utilisateur pourra le saisir manuellement
      }
    },

    copyLink() {
      if (this.messageLink) {
        navigator.clipboard.writeText(this.messageLink);
        this.$store.commit('setSnackBarMessage', 'Referral link copied!');
        this.$store.commit('setSnackBarColor', 'teal darken-4');
        this.$store.commit('activateSnackBar', true);
      }
    },

    async addMessage() {
      if (this.messageTitle === "") {
        this.$store.commit('setSnackBarMessage', "Please fill in the message name");
        this.$store.commit('setSnackBarColor', "red darken-4");
        this.$store.commit('activateSnackBar', true);
        this.$refs.message_title.focus();
      }
      else if (this.messageContent === "") {
        this.$store.commit('setSnackBarMessage', "Please input the message description");
        this.$store.commit('setSnackBarColor', "red darken-4");
        this.$store.commit('activateSnackBar', true);
        this.$refs.message_content.focus();
      }
      else if (!this.messageFile) {
        this.$store.commit('setSnackBarMessage', "Please upload the message file");
        this.$store.commit('setSnackBarColor', "red darken-4");
        this.$store.commit('activateSnackBar', true);
        this.$refs.message_file.focus();
      }
      else {
        this.loading = true;
        let formData = new FormData();
        formData.append('messageFile', this.messageFile);
        formData.append('messageContent', this.messageContent);
        formData.append('messageLink', this.messageLink);
        formData.append('messageTitle', this.messageTitle);

        try {
          const response = await axios.post('/api/v1/topup/add/marketing_message/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          this.$store.commit('setSnackBarMessage', response.data[0].text);
          this.$store.commit('setSnackBarColor', response.data[0].color);
          this.$store.commit('activateSnackBar', true);
          if (response.data[0].id !== 0) {
            setTimeout(() => {
              this.$router.push({ name: 'savings.marketing_prospects' });
            }, 4000);
          }
        } catch (error) {
          if (error.response) {
            this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText);
            this.$store.commit('setSnackBarColor', "red darken-4");
            this.$store.commit('activateSnackBar', true);
          } else {
            this.$store.commit('setSnackBarMessage', "An error occured, please check your internet connection and try again later.");
            this.$store.commit('setSnackBarColor', "red darken-4");
            this.$store.commit('activateSnackBar', true);
          }
        } finally {
          this.loading = false;
        }
      }
    },
  },
}
</script>

<style scoped>
.v-card {
  border-radius: 12px;
  background: #ffffff;
}
</style>
